// Elintra — Zustand Store | Wave 4 | Gemini Fix Applied
// CRITICAL: useAllQueries uses split selectors + useMemo.
// Raw spread [...live,...hist] in a Zustand selector causes getSnapshot
// infinite loop in React 18 — new array reference on every check.

import { create } from 'zustand';
import { useMemo } from 'react';
import type {
  Route, User, AgentQuery, ProtocolHealthItem, StoreRecord,
  ChartDataPoint, PaymentRailHealthItem, WsStatus, DataResource,
} from '@/types/app';
import {
  PROTOCOL_HEALTH_FALLBACK, PAYMENT_RAILS_FALLBACK,
  STORE_FIXTURES, generateChartData, generateMockQuery,
} from '@/lib/fixtures';
import {
  uagFetchHealth, uagFetchAgentQueries, uagFetchProtocolHealth,
  uagFetchStores, uagFetchAnalyticsChart, uagFetchPaymentRails,
  uagCreateFeedConnection, UagError, isFixturesMode, HEALTH_FALLBACK,
} from '@/lib/uagClient';
import type { FeedConnection } from '@/lib/uagClient';
import { sumMoneyStrings } from '@/lib/utils';

const NO_ERRORS: Record<DataResource, string | null> = {
  queries: null, protocols: null, stores: null,
  chart:   null, rails:     null, health: null,
};

interface AppStoreState {
  route:             Route;
  user:              User | null;
  sessionToken:      string;
  historicalQueries: AgentQuery[];
  liveQueries:       AgentQuery[];
  protocolHealth:    ProtocolHealthItem[];
  stores:            StoreRecord[];
  chartData:         ChartDataPoint[];
  paymentRails:      PaymentRailHealthItem[];
  intentGraphSize:   number;
  x402PaymentCount:  number;        // v3.0.0: total x402 payments on elintra.algo
  algoSettledUsdt:   string;        // v3.0.0: total USDT settled via Algorand
  isLiveFeedActive:  boolean;
  wsStatus:          WsStatus;
  feedConnection:    FeedConnection | null;
  isBootLoading:     boolean;
  errors:            Record<DataResource, string | null>;
  setRoute:          (route: Route) => void;
  setUser:           (user: User | null) => void;
  addLiveQuery:      (query: AgentQuery) => void;
  setLiveFeedActive: (active: boolean) => void;
  setWsStatus:       (status: WsStatus) => void;
  seedQueries:       () => void;
  loadAppData:       () => Promise<void>;
  connectFeed:       () => void;
  disconnectFeed:    () => void;
  clearError:        (resource: DataResource) => void;
}

let mockIntervalHandle: ReturnType<typeof setInterval> | null = null;

// The synthetic mock-seed (offline demo) is gated to FIXTURES-MODE only. Against
// a live backend (VITE_UAG_BASE_URL set, VITE_USE_FIXTURES unset) it never runs,
// so the real fetched /agent-queries rows are what render — not mock hashes.
function startMockFeed(getState: () => AppStoreState) {
  if (!isFixturesMode())          return;
  if (mockIntervalHandle !== null) return;
  mockIntervalHandle = setInterval(() => {
    const s = getState();
    if (!s.isLiveFeedActive)        return;
    if (s.wsStatus === 'connected') return;
    s.addLiveQuery(generateMockQuery());
  }, 3_500);
}

export const useAppStore = create<AppStoreState>((set, get) => ({
  route:             '' as Route,
  user:              null,
  sessionToken:      'dev-session-key',
  historicalQueries: [],
  liveQueries:       [],
  protocolHealth:    PROTOCOL_HEALTH_FALLBACK,
  stores:            STORE_FIXTURES,
  chartData:         generateChartData(),
  paymentRails:      PAYMENT_RAILS_FALLBACK,
  intentGraphSize:   142_589,
  x402PaymentCount:  0,              // v3.0.0: starts at 0, grows with live x402 traffic
  algoSettledUsdt:   '0.00',         // v3.0.0: starts at 0.00
  isLiveFeedActive:  true,
  wsStatus:          'disconnected',
  feedConnection:    null,
  isBootLoading:     false,
  errors:            { ...NO_ERRORS },

  setRoute: (route) => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    set({ route });
    startMockFeed(get);
  },

  setUser: (user) => set({
    user,
    sessionToken: user?.sessionToken ?? 'dev-session-key',
  }),

  addLiveQuery: (query) =>
    set((s) => ({ liveQueries: [query, ...s.liveQueries].slice(0, 500) })),

  setLiveFeedActive: (active) => set({ isLiveFeedActive: active }),
  setWsStatus:       (wsStatus) => set({ wsStatus }),

  seedQueries: () => {
    // Synthetic seeding is FIXTURES-MODE only — against a live backend the
    // historical feed is the real fetched /agent-queries rows, never mocks.
    if (!isFixturesMode()) return;
    if (get().historicalQueries.length > 0) { startMockFeed(get); return; }
    const seeded = Array.from({ length: 20 }, generateMockQuery).reverse();
    set({ historicalQueries: seeded });
    startMockFeed(get);
  },

  loadAppData: async () => {
    const { sessionToken } = get();
    set({ isBootLoading: true, errors: { ...NO_ERRORS } });

    // Platform-wide chart: no creator identity needed. Health and chart are both
    // live; health currently omits the counters, so we derive them from the chart.
    const [healthRes, queriesRes, protocolsRes, storesRes, chartRes, railsRes] =
      await Promise.allSettled([
        uagFetchHealth(),
        uagFetchAgentQueries(sessionToken),
        uagFetchProtocolHealth(sessionToken),
        uagFetchStores(sessionToken),
        uagFetchAnalyticsChart(sessionToken, undefined, 30),
        uagFetchPaymentRails(sessionToken),
      ]);

    const errors  = { ...NO_ERRORS };
    const updates: Partial<AppStoreState> = { isBootLoading: false };

    // Counters live in the chart series on this edge (/health is {status,service}).
    // Derive x402 count + USDT settled from the live chart as the canonical source.
    const liveChart   = chartRes.status === 'fulfilled' ? chartRes.value : null;
    const chartX402   = liveChart?.reduce((s, d) => s + (d.x402_payments ?? 0), 0) ?? null;
    const chartUsdt   = liveChart ? sumMoneyStrings(liveChart.map((d) => d.algo_volume_usdt)) : null;

    if (healthRes.status === 'fulfilled') {
      const h = healthRes.value;
      // Prefer the explicit /health field; else the live-chart derivation; else fixture.
      updates.intentGraphSize  = h.intentGraphSize  ?? HEALTH_FALLBACK.intentGraphSize;
      updates.x402PaymentCount = h.x402PaymentCount ?? chartX402 ?? HEALTH_FALLBACK.x402PaymentCount;
      updates.algoSettledUsdt  = h.algoSettledUsdt  ?? chartUsdt ?? HEALTH_FALLBACK.algoSettledUsdt;
    } else {
      errors.health = (healthRes.reason as UagError).message ?? 'Health check failed';
      // Health failed but the chart may still be live → keep counters truthful.
      if (chartX402 != null) updates.x402PaymentCount = chartX402;
      if (chartUsdt != null) updates.algoSettledUsdt  = chartUsdt;
    }

    if (queriesRes.status === 'fulfilled' && queriesRes.value.length > 0)
      updates.historicalQueries = queriesRes.value;
    else if (queriesRes.status === 'rejected') {
      errors.queries = (queriesRes.reason as UagError).message ?? 'Failed to load queries';
      if (get().historicalQueries.length === 0)
        updates.historicalQueries = Array.from({ length: 20 }, generateMockQuery).reverse();
    }

    if (protocolsRes.status === 'fulfilled') updates.protocolHealth = protocolsRes.value;
    else errors.protocols = (protocolsRes.reason as UagError).message ?? 'Protocol health unavailable';

    if (storesRes.status === 'fulfilled') updates.stores = storesRes.value;
    else errors.stores = (storesRes.reason as UagError).message ?? 'Stores unavailable';

    if (chartRes.status === 'fulfilled') updates.chartData = chartRes.value;
    else errors.chart = (chartRes.reason as UagError).message ?? 'Chart data unavailable';

    if (railsRes.status === 'fulfilled') updates.paymentRails = railsRes.value;
    else errors.rails = (railsRes.reason as UagError).message ?? 'Payment rails unavailable';

    set({ ...updates, errors });
    startMockFeed(get);
  },

  connectFeed: () => {
    const { user, sessionToken, feedConnection } = get();
    if (!user || feedConnection) return;
    const conn = uagCreateFeedConnection(user.id, sessionToken, {
      onQuery:        (q) => get().addLiveQuery(q),
      onStatusChange: (s) => get().setWsStatus(s),
      onError:        (e) => console.error('[UAG Feed]', e.message),
    });
    set({ feedConnection: conn });
  },

  disconnectFeed: () => {
    get().feedConnection?.close();
    set({ feedConnection: null, wsStatus: 'disconnected' });
    if (mockIntervalHandle) { clearInterval(mockIntervalHandle); mockIntervalHandle = null; }
  },

  clearError: (resource) =>
    set((s) => ({ errors: { ...s.errors, [resource]: null } })),
}));

// ─── Selector Hooks ── GEMINI FIX: split selectors + useMemo ─────────────────

export const useAllQueries = () => {
  const live = useAppStore((s) => s.liveQueries);
  const hist = useAppStore((s) => s.historicalQueries);
  return useMemo(() => [...live, ...hist].slice(0, 500), [live, hist]);
};

export const useQueryStats = () => {
  const all = useAllQueries();
  return useMemo(() => {
    const purchased = all.filter((q) => q.outcome === 'PURCHASED');
    const matched   = all.filter((q) => q.outcome !== 'REJECTED');
    // amount is a decimal STRING — sum via integer-cents math, never a float.
    const revenue   = sumMoneyStrings(purchased.map((q) => q.amount));
    const matchRate = all.length ? Math.round((matched.length / all.length) * 100) : 0;
    return { total: all.length, revenue, matchRate, purchases: purchased.length, aeoScore: matchRate };
  }, [all]);
};
